import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Row, Col, Card, CardBody, Button, InputGroup, Input } from "reactstrap";

import DataTable from "react-data-table-component";

import { Slide, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

//import common page container
import PageContainer from "../Layout/PageContainer";

import styles from "../../assets/subHeader.module.scss";

//import refund service
import refundService from "../../redux/services/refund.service";

//import common filter component
import FilterComponent from "../../helpers/FilterComponent";

import debounceFunction from "../../helpers/Debounce";
import { convertDate, getDateTimeInCST, getSpaceAdddress } from "../../helpers/functions";

import IconContainer from "../Common/IconContainer";
import { AiFillEye } from "react-icons/ai";

toast.configure();

const RefundListing = (props) => {
    const navigate = useNavigate();
    const role = props.currentUser.roleId === 1 ? "admin" : "csr";
    const [refunds, setRefunds] = useState([]);
    const [totalRefunds, setTotalRefunds] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterText, setFilterText] = useState("");

    const fetchRefunds = async (keyword, page, perPageObj) => {
        try {
            const response = await refundService.getRefundsListing(keyword, page, perPageObj);
            if (response && response.data && response.data.listRefunds) {
                setRefunds(response.data.listRefunds.rows || []);
                setTotalRefunds(response.data.listRefunds.count || 0);
            }
        } catch (error) {
            console.error("Error fetching refunds:", error);
            toast.error(error?.response?.data?.message || "Failed to fetch refunds.");
        }
    };

    useEffect(() => {
        //fetch refunds
        fetchRefunds(filterText, 1, perPage);
    }, []);

    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

    const debounceSearch = useCallback(
        debounceFunction((nextValue) => {
            fetchRefunds(nextValue, 1, perPage);
        }, 1000),
        [perPage]
    );

    const handleExport = async () => {
        try {
            if (role === "admin") {
                const response = await refundService.exportRefunds({});
                const blob = new Blob([response.data], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `refunds_export_${getDateTimeInCST()}.csv`);

                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);
            } else {
                toast("Only Admin users are allowed to perform this action!", {
                    transition: Slide,

                    closeButton: true,

                    autoClose: 3000,

                    position: "top-right",

                    type: "error",
                });
            }
        } catch (error) {
            console.error("Export failed", error);
        }
    };

    //subheader component of react-data-table.
    const subHeaderComponent = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText("");
                fetchRefunds("", 1, perPage);
            }
        };

        return (
            <div className="w-100 d-flex justify-content-between align-items-center">
                <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                    Refund Listing
                </div>
                <div className="d-flex align-items-center justify-content-end" style={{ width: "60%" }}>
                    <InputGroup size="sm" style={{ width: "250px", marginBottom: 0 }}>
                        <Input
                            id="search"
                            type="text"
                            placeholder="Search..."
                            value={filterText}
                            onChange={(event) => {
                                setFilterText(event.target.value);
                                debounceSearch(event.target.value);
                            }}
                        />
                        <Button color="primary" onClick={handleClear}>
                            <i className="pe-7s-close" style={{ cursor: "pointer", fontWeight: "bold" }}></i>
                        </Button>
                    </InputGroup>
                    {/* Actions - Right Side */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <Button color="primary" size="sm" onClick={handleExport} className="me-2 text-nowrap">
                            <i className="pe-7s-cloud-download me-1"></i> Export
                        </Button>
                    </div>
                </div>
            </div>
        );
    }, [filterText, resetPaginationToggle, perPage, debounceSearch]);

    const columns = useMemo(
        () => [
            {
                name: "Reservation Code",
                width: "180px",
                selector: (row) => {
                    return row.Reservation?.reservationCode || "N/A";
                },
                sortable: true,
            },
            {
                name: "Transaction Code",
                width: "250px",
                selector: (row) => {
                    return row.Refunds?.[0]?.transactionCode;
                },
                sortable: true,
            },
            {
                name: "Space Name",
                width: "300px",
                minWidth: "300px",
                toolTip: true,
                title: (row) => {
                    return getSpaceAdddress(row.Reservation?.Space);
                },
                style: {
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "12px",
                },
                selector: (row) => {
                    return getSpaceAdddress(row.Reservation?.Space);
                },
                sortable: true,
            },
            {
                name: "Rate",
                width: "120px",
                selector: (row) => {
                    return `$${row.rate}`;
                },
                sortable: true,
            },
            {
                name: "Gross Amount",
                width: "130px",
                selector: (row) => {
                    return `$${row.grossAmount || 0}`;
                },
                sortable: true,
            },
            {
                name: "Refund Amount",
                width: "130px",
                selector: (row) => {
                    return `$${row.Refunds?.[0]?.refundAmount || 0}`;
                },
                sortable: true,
            },
            {
                name: "Refund Type",
                width: "120px",
                selector: (row) => {
                    return row.Refunds?.[0]?.refundType || "N/A";
                },
                sortable: true,
            },
            {
                name: "Refund Reason",
                width: "250px",
                selector: (row) => {
                    return row.Refunds?.[0]?.refundReason || "N/A";
                },
                sortable: true,
            },
            {
                name: "Refund Notes",
                width: "300px",
                selector: (row) => {
                    return row.Refunds?.[0]?.refundNotes || "N/A";
                },
                sortable: true,
            },
            {
                name: "Payment Date",
                width: "182px",
                selector: (row) => {
                    return convertDate(new Date(row.paymentDate));
                },
                sortable: true,
            },
            {
                name: "Refund Date",
                width: "182px",
                selector: (row) => {
                    return convertDate(new Date(row.Refunds?.[0]?.createdAt));
                },
                sortable: true,
            },
            {
                name: "Actions",
                button: true,
                minWidth: "140px",
                cell: (row) => (
                    <div className="d-flex justify-content-center">
                        <IconContainer
                            id={"viewIcon_" + row?.id}
                            Icon={AiFillEye}
                            handleOnClick={() => {
                                navigate(`/refunds/view/${row?.id}`);
                            }}
                            toolTip={"View refund details"}
                        />
                    </div>
                ),
            },
        ],
        []
    );

    const handlePageChange = (page) => {
        fetchRefunds(filterText, page, perPage);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        fetchRefunds(filterText, page, newPerPage);
        setPerPage(newPerPage);
    };

    return (
        <PageContainer
            pageTitleIcon="pe-7s-repeat icon-gradient bg-plum-plate"
            pageHeading="Refunds"
            pageSubTitle="Listing all the refunds"
        >
            <Row>
                <Col md="12">
                    <Card className="main-card mb-3">
                        <CardBody>
                            <DataTable
                                columns={columns}
                                data={refunds}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRefunds}
                                paginationDefaultPage={currentPage}
                                onChangeRowsPerPage={handlePerRowsChange}
                                onChangePage={handlePageChange}
                                subHeader
                                subHeaderComponent={subHeaderComponent}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </PageContainer>
    );
};

export default RefundListing;
